import { useState, useEffect, MouseEvent } from 'react';
import { Loading } from 'framework/ui/mui-builder';
import { usePrevious } from 'framework/utils';
import { AsyncLightboxProps } from './lightboxTypes';
import { Box, IconButton, Typography } from '@mui/material';
import ArrowBackIosNew from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos';
import Close from '@mui/icons-material/Close';
import Download from '@mui/icons-material/Download';

const KEYS = {
  ESC: 27,
  LEFT_ARROW: 37,
  RIGHT_ARROW: 39,
  ENTER: 13,
};

const BODY = document.body;

const addBodyStyles = (paddingRight: number) => {
  BODY.style.overflow = 'hidden';
  BODY.style.paddingRight = `${paddingRight}px`;
};

const removeBodyStyles = () => {
  BODY.style.overflow = '';
  BODY.style.paddingRight = '0';
};

const Lightbox = ({ initial, photos, onClose, onDownload = null }: AsyncLightboxProps) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paddingRight] = useState(window.innerWidth - document.documentElement.clientWidth);
  const [error, setError] = useState<Nullable<string>>(null);
  const [image, setImage] = useState<any>(null);

  const prevActiveIndex: any = usePrevious(activeIndex);

  function movePrev() {
    setLoading(true);
    setActiveIndex((prevActiveIndex + photos.length - 1) % photos.length);
  }

  function moveNext() {
    setLoading(true);
    setActiveIndex((prevActiveIndex + 1) % photos.length);
  }

  function handleClick(e: MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }

  function handleKeyDown(e: KeyboardEvent) {
    const key = e.keyCode;

    if (key === KEYS.ESC) {
      onClose();
    }
    if (key === KEYS.LEFT_ARROW) {
      movePrev();
    }
    if (key === KEYS.RIGHT_ARROW) {
      moveNext();
    }
  }

  useEffect(() => {
    setOpen(true);
    setActiveIndex(initial);

    if (image && image.naturalHeight > 0) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    addBodyStyles(paddingRight);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      removeBodyStyles();
    };
  });

  const controlSx = {
    position: 'absolute',
    width: '30px',
    height: '30px',
    padding: 0,
    color: 'background.default',
    '&:focus': { outline: 'none' },
  } as const;

  return (
    <Box
      data-testid="lightbox"
      onClick={handleClick}
      role="presentation"
      sx={{
        alignItems: 'center',
        backgroundColor: 'common.shadowContrast',
        display: 'flex',
        justifyContent: 'center',
        height: '100vh',
        inset: 0,
        opacity: open ? 1 : 0,
        padding: '30px 70px',
        position: 'fixed',
        zIndex: 1020,
        ...(open && { transition: 'opacity 0.3s ease' }),
      }}
    >
      {photos.length > 1 && (
        <IconButton
          onClick={movePrev}
          aria-label="Previous photo"
          sx={{ ...controlSx, top: 'calc(50% - 20px)', left: '15px' }}
        >
          <ArrowBackIosNew />
        </IconButton>
      )}
      <Box
        component="img"
        ref={node => {
          setImage(node);
        }}
        data-testid="lightbox-img"
        alt=""
        src={photos[activeIndex]?.src}
        onLoad={() => setLoading(false)}
        onError={() => {
          setError("Error occured, can't show the photo");
        }}
        sx={{
          maxHeight: '100%',
          maxWidth: '100%',
          opacity: loading || error ? 0 : 1,
          transition: 'opacity 0.3s ease',
        }}
      />

      {loading ? <Loading /> : ''}

      {!loading && error ? (
        <Typography
          component="div"
          variant="inherit"
          sx={{
            color: 'background.default',
            left: '50%',
            position: 'absolute',
            top: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          {error}
        </Typography>
      ) : (
        ''
      )}

      {photos.length > 1 && (
        <IconButton
          onClick={moveNext}
          aria-label="Next photo"
          sx={{ ...controlSx, top: 'calc(50% - 20px)', right: '15px' }}
        >
          <ArrowForwardIos />
        </IconButton>
      )}

      <IconButton
        onClick={() => {
          onClose();
        }}
        aria-label="Close"
        sx={{ ...controlSx, height: '20px', width: '20px', right: '10px', top: '10px' }}
      >
        <Close />
      </IconButton>
      {onDownload && (
        <IconButton
          onClick={() => {
            const activePhoto = photos[activeIndex] || {};

            onDownload(activePhoto.id, activePhoto.title);
          }}
          aria-label="Download"
          sx={{ ...controlSx, fontSize: '20px', bottom: '5px', right: '5px' }}
        >
          <Download />
        </IconButton>
      )}
    </Box>
  );
};

export default Lightbox;
